import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { api, getAdminToken } from "@/lib/api";
import { Stars } from "@/components/ReaderReviews";

const auth = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });
const EMPTY = { name: "", detail: "", text: "", rating: 5, visible: true, order: 0 };

export default function AdminReviews() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/reviews", auth()).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (r = edit) => {
    try {
      const body = { name: r.name, detail: r.detail || "", text: r.text, visible: r.visible, rating: Number(r.rating), order: Number(r.order) || 0 };
      if (r.id) await api.put(`/admin/reviews/${r.id}`, body, auth());
      else await api.post("/admin/reviews", body, auth());
      toast.success("Avis enregistré"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.status === 422 ? "Nom et texte requis (600 caractères max)" : "Enregistrement impossible"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Supprimer l'avis de ${r.name} ?`)) return;
    await api.delete(`/admin/reviews/${r.id}`, auth()); load();
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-2xl text-[#1F2A24]">Avis lecteurs</h3>
        <button data-testid="admin-review-add" onClick={() => setEdit(EMPTY)} className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-5 py-2.5 font-ui text-sm hover:bg-[#2c4a2e]"><Plus size={16} /> Nouvel avis</button>
      </div>
      <p className="font-sans text-xs text-[#1F2A24]/55 mb-5">Les avis visibles (6 au maximum) s'affichent sous le Coffret Découverte. Publiez uniquement des avis réels, avec l'accord de leurs auteurs. 21 jours après leur achat, les clients reçoivent une invitation par e-mail : leurs avis arrivent ici « En attente » et ne sont publiés qu'après votre validation.</p>
      {list.some((r) => r.pending) && <p className="mb-4 rounded-xl bg-[#C9A24B]/15 text-[#8a6a22] px-4 py-2 font-sans text-sm" data-testid="admin-reviews-pending-count">{list.filter((r) => r.pending).length} avis en attente de validation</p>}
      {edit && <ReviewForm r={edit} setR={setEdit} onSave={() => save()} />}
      <div className="space-y-3 mt-4">
        {list.length === 0 && !edit && <p className="font-sans text-sm text-[#1F2A24]/50" data-testid="admin-reviews-empty">Aucun avis pour l'instant. La section reste masquée sur le site tant qu'aucun avis n'est visible.</p>}
        {list.map((r) => (
          <div key={r.id} className={`rounded-xl bg-white border border-[#E8DCC4] p-4 flex flex-wrap gap-3 justify-between ${r.visible || r.pending ? "" : "opacity-60"}`} data-testid={`admin-review-${r.id}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="font-serif text-lg text-[#1F2A24]">{r.name} <span className="font-sans text-xs text-[#1F2A24]/50">{r.detail}</span>{r.pending && <span className="ml-2 rounded-full bg-[#C9A24B]/20 text-[#8a6a22] px-2 py-0.5 font-ui text-[11px]" data-testid={`admin-review-pending-${r.id}`}>En attente</span>}{r.source === "email" && !r.pending && <span className="ml-2 font-ui text-[11px] text-[#1F2A24]/40">reçu par e-mail</span>}</p>
              <Stars n={r.rating} />
              <p className="font-sans text-sm text-[#1F2A24]/75 mt-1 line-clamp-2">{r.text}</p>
            </div>
            <div className="flex gap-1 items-start">
              {r.pending && <button onClick={() => save({ ...r, visible: true })} data-testid={`admin-review-approve-${r.id}`} className="rounded-full bg-[#385E3A] text-white px-3 py-1 text-xs">Publier</button>}
              <button onClick={() => save({ ...r, visible: !r.visible })} data-testid={`admin-review-toggle-${r.id}`} title={r.visible ? "Masquer" : "Afficher"} className="p-1.5 rounded border">{r.visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
              <button onClick={() => setEdit(r)} data-testid={`admin-review-edit-${r.id}`} className="p-1.5 rounded border"><Pencil size={14} /></button>
              <button onClick={() => del(r)} data-testid={`admin-review-delete-${r.id}`} className="p-1.5 rounded border text-[#A63B36]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ReviewForm = ({ r, setR, onSave }) => {
  const set = (k) => (e) => setR({ ...r, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="rounded-xl bg-white border-2 border-[#C9A24B] p-4 grid gap-3" data-testid="admin-review-form">
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="border rounded px-3 py-2 text-sm" placeholder="Prénom / nom *" maxLength={60} value={r.name} onChange={set("name")} data-testid="admin-review-name" />
        <input className="border rounded px-3 py-2 text-sm" placeholder="Précision (ex : lectrice, Lyon)" maxLength={80} value={r.detail} onChange={set("detail")} data-testid="admin-review-detail" />
        <select className="border rounded px-3 py-2 text-sm bg-white" value={r.rating} onChange={set("rating")} data-testid="admin-review-rating">
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
        </select>
      </div>
      <textarea className="border rounded px-3 py-2 text-sm" rows={3} maxLength={600} placeholder="Texte de l'avis *" value={r.text} onChange={set("text")} data-testid="admin-review-text" />
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={r.visible} onChange={set("visible")} data-testid="admin-review-visible" /> Visible sur le site</label>
        <label className="inline-flex items-center gap-2">Ordre <input className="border rounded px-2 py-1 w-16" value={r.order} onChange={set("order")} /></label>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setR(null)} className="rounded-full border px-5 py-2">Annuler</button>
          <button onClick={onSave} data-testid="admin-review-save" className="rounded-full bg-[#385E3A] text-white px-5 py-2">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};
